import React, { useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import Button from "../../../../component-lib/Molecules/button/Button";
import SelectDropdown from "../../../../component-lib/Atoms/Select/Select";
import EventEHRCheckbox from "../../../../component-lib/Molecules/checkbox/EventEHRCheckbox";
import { TEXT_CONSTANTS } from "../../../../utils/text-constants/textConstants";
import { componentKey } from "../../patientSlice";
import { getRoomList, assignRoom } from "../../patientSaga";
import { getRoomDisplayName } from "../../../../utils/room/roomDisplay";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: "16px 24px",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    width: "560px",
    maxWidth: "none",
  },
}));

interface RoomAssignmentDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  patientId?: string;
  visitId?: string;
  facilityId?: string;
  // The patient's current room assignment (from viewPatientProfileData.room).
  currentRoom?: any;
}

const ROOM_FIELD = "roomId";
const WAITING_ROOM_FIELD = "assignToWaitingRoom";

export default function RoomAssignmentDialog({
  isOpen = false,
  handleClose,
  patientId,
  visitId,
  facilityId,
  currentRoom,
}: RoomAssignmentDialogProps) {
  const dispatch = useDispatch();
  const { roomList } = useSelector((state: any) => state[componentKey]);

  useEffect(() => {
    if (isOpen && facilityId) {
      dispatch(getRoomList({ facilityId, limit: 100 }));
    }
  }, [isOpen, facilityId]);

  const assignedRoom = currentRoom?.room;

  // Only real, available rooms are selectable: exclude Expected and Waiting rooms
  // (backend-managed) and any room that is already at capacity.
  const availableRooms = (roomList || []).filter(
    (room: any) =>
      room?.isExpectedRoom !== true &&
      room?.isWaitingRoom !== true &&
      !(room?.capacity != null && room?.currentOccupancy >= room?.capacity),
  );

  const roomOptions = availableRooms.map((room: any) => ({
    label: `${room?.name}${room?.roomNumber ? ` (${room?.roomNumber})` : ""}${
      room?.capacity != null
        ? ` — ${room?.currentOccupancy ?? 0}/${room?.capacity}`
        : ""
    }`,
    value: room?.id,
  }));

  const noRoomsAvailable = availableRooms.length === 0;

  const validationSchema = Yup.object().shape({
    [ROOM_FIELD]: Yup.string().when(WAITING_ROOM_FIELD, {
      is: false,
      then: (schema) => schema.required("Please select a room"),
    }),
  });

  const handleAssign = (values: any) => {
    const assignToWaitingRoom = values[WAITING_ROOM_FIELD] === true;
    dispatch(
      assignRoom({
        apiPayload: {
          patientId,
          visitId,
          roomId: assignToWaitingRoom ? undefined : values[ROOM_FIELD],
          assignToWaitingRoom,
        },
        patientId,
      }),
    );
    handleClose();
  };

  return (
    <BootstrapDialog
      onClose={(_event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="room-assignment-dialog-title"
      open={isOpen}
    >
      <Formik
        initialValues={{
          [ROOM_FIELD]: assignedRoom?.id || "",
          [WAITING_ROOM_FIELD]: false,
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          try {
            handleAssign(values);
          } catch (error) {
            console.log(error);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          values,
          touched,
          setFieldValue,
          handleBlur,
          handleSubmit,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="room-assignment-dialog-title"
              >
                Room Assignment
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />
              <DialogContent>
                {/* CURRENT ROOM */}
                <div className="mb-6">
                  <div className="font-size-14px font-weight-500 color-565656 mb-1">
                    Current Room
                  </div>
                  <div className="font-size-14px color-1D1D1D">
                    {getRoomDisplayName(assignedRoom) || "Not assigned"}
                  </div>
                </div>

                {/* ROOM DROPDOWN */}
                <div>
                  <div className="font-size-14px font-weight-500 color-565656 mb-1">
                    Assign Room
                  </div>
                  <SelectDropdown
                    label=""
                    displayEmpty
                    placeholderValue="Select a room"
                    value={values[ROOM_FIELD]}
                    options={roomOptions}
                    onChangeCb={(e) =>
                      setFieldValue(ROOM_FIELD, e.target.value)
                    }
                    onBlur={handleBlur}
                    disabled={values[WAITING_ROOM_FIELD] || noRoomsAvailable}
                    error={touched[ROOM_FIELD] && Boolean(errors[ROOM_FIELD])}
                    helperText={
                      touched[ROOM_FIELD]
                        ? (errors[ROOM_FIELD] as string)
                        : undefined
                    }
                  />
                  {noRoomsAvailable && (
                    <div
                      className="font-size-12px mt-1"
                      style={{ color: "#D32F2F" }}
                    >
                      No rooms are available — all rooms are occupied. Please keep
                      the patient in the waiting room.
                    </div>
                  )}
                </div>
              </DialogContent>

              {/* ASSIGN TO WAITING ROOM */}
              <div className="mb-4 ml-6 mt-2">
                <EventEHRCheckbox
                  label="Assign to Waiting Room"
                  checkboxSelected={values[WAITING_ROOM_FIELD]}
                  callback={() => {
                    const next = !values[WAITING_ROOM_FIELD];
                    setFieldValue(WAITING_ROOM_FIELD, next);
                    // Clear any selected room when switching to the waiting room.
                    if (next) {
                      setFieldValue(ROOM_FIELD, "");
                    }
                  }}
                />
              </div>

              <DialogActions style={{ padding: "24px" }}>
                <Button
                  label={TEXT_CONSTANTS?.CANCEL}
                  variant="outlined"
                  onClickCb={handleClose}
                  style={{
                    width: "120px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                    backgroundColor: "white",
                    border: "none",
                    color: "#393939",
                  }}
                />
                <Button
                  type="submit"
                  label="Assign"
                  variant="contained"
                  onClickCb={() => {}}
                  style={{ padding: "8px 55px", fontSize: "16px" }}
                />
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </BootstrapDialog>
  );
}
